import React, { useEffect, useRef, useState } from 'react';
import { DKLabel } from 'deskera-ui-library';

interface IPasswordInputProps {
  title: string;
  value: string;
  onChange: (text: string) => void;
  canValidate?: boolean;
  className?: string;
  /* 'current-password' lets the browser offer the saved password for this field */
  autoComplete?: 'current-password' | 'new-password';
}

const EyeIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="gray"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="gray"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const PasswordInput: React.FC<IPasswordInputProps> = ({
  title,
  value,
  onChange,
  canValidate,
  className,
  autoComplete = 'new-password'
}) => {
  const [visible, setVisible] = useState(false);
  /* fields stay read-only until the user focuses them, so nothing can be auto-filled on open */
  const [editable, setEditable] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const allowAutofill = autoComplete === 'current-password';
  const invalid = Boolean(canValidate) && value.trim().length === 0;

  /* an autofill on open does not always reach React, so pick the value up from the DOM */
  useEffect(() => {
    if (!allowAutofill) return;
    const timer = setTimeout(() => {
      const filled = inputRef.current?.value;
      if (filled) onChange(filled);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={'column parent-width ' + (className || '')}>
      <div className="row-auto-width mb-xs">
        <DKLabel
          className="unselectable"
          text={`${title} *`}
          style={{ color: 'gray' }}
        />
      </div>
      <div
        className="row border-box border-radius-s dkinput-textfield-height bg-dk-input"
        style={{
          border: invalid
            ? '1.2px solid rgba(201, 34, 45, 0.7)'
            : '1.2px solid rgb(235, 235, 235)'
        }}
      >
        <input
          ref={inputRef}
          type={visible ? 'text' : 'password'}
          className="border-none app-font p-s border-box"
          style={{
            outline: 'none',
            backgroundColor: 'transparent',
            flex: 1,
            minWidth: 0
          }}
          value={value}
          spellCheck={false}
          autoComplete={autoComplete}
          readOnly={!allowAutofill && !editable}
          onFocus={() => setEditable(true)}
          /* password managers ignore the autocomplete hint, so opt the new-password fields out */
          {...(allowAutofill
            ? {}
            : {
                'data-lpignore': 'true',
                'data-1p-ignore': 'true',
                'data-form-type': 'other'
              })}
          onChange={(e) => onChange(e.target.value)}
        />
        <div
          className="cursor-hand pl-xs row-auto-width"
          title={visible ? 'Hide password' : 'Show password'}
          onClick={() => setVisible(!visible)}
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </div>
      </div>
      {invalid && (
        <div className="position-relative parent-width">
          <DKLabel
            text="Please fill out this field"
            className="text-red ml-xs mt-xs position-absolute"
            style={{ fontSize: 11 }}
          />
        </div>
      )}
    </div>
  );
};

export default PasswordInput;
